import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ChevronRight,
  Ruler,
  Layers,
  Zap,
  ShieldCheck,
  Sparkles,
  Target,
  Beaker,
} from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import RegistrationMark from '../components/RegistrationMark';

const ACCENT = '#0F7B3F';

const HIGHLIGHTS = [
  {
    title: 'Automated XY scanning',
    desc:
      'Motorized measurement of full ISO test charts with sub-millimeter positioning — thousands of patches, unattended.',
    Icon: Ruler,
  },
  {
    title: 'Any substrate, any ink',
    desc:
      'Paper, film, textile, ceramic, transparent, metallic and fluorescent — one instrument replaces a whole toolbox.',
    Icon: Layers,
  },
  {
    title: 'Reference-grade accuracy',
    desc:
      'Inter-instrument agreement ΔE00 < 0.4 average. Traceable to national standards (PTB / NIST-equivalent).',
    Icon: Target,
  },
  {
    title: 'M0 · M1 · M2 · M3',
    desc:
      'Full ISO 13655 measurement conditions in a single pass, including OBA compensation and polarization filter.',
    Icon: Beaker,
  },
  {
    title: 'Built for production',
    desc:
      'Rugged Italian engineering. Designed to run 3 shifts a day inside real print, textile and label factories.',
    Icon: ShieldCheck,
  },
  {
    title: 'Plug into any workflow',
    desc:
      'Native connectors to PrintFactory, ColorGATE, EFI, Onyx, Caldera, GMG and i1Profiler over USB or Wi-Fi.',
    Icon: Zap,
  },
];

const DEVICES = [
  {
    id: 'lfp',
    name: 'Spectro LFP qb',
    tag: 'Flagship',
    desc:
      'The industry benchmark for large-format & industrial printing. Motorized XY table, up to 1,650 patches/min, textile & ceramic support.',
    stats: [
      ['Aperture', '2 · 4 · 6 · 8 mm'],
      ['Speed', '1,650 patches/min'],
      ['Modes', 'M0 · M1 · M2 · M3'],
    ],
  },
  {
    id: 'swing',
    name: 'Spectro Swing',
    tag: 'Sheetfed',
    desc:
      'Compact automated spectrophotometer for offset, digital and packaging. Single-button charts in seconds.',
    stats: [
      ['Aperture', '2 · 4 · 6 mm'],
      ['Format', 'A5 · A4 · A3'],
      ['Modes', 'M0 · M1 · M2'],
    ],
  },
  {
    id: 'pad',
    name: 'Spectropad',
    tag: 'Portable',
    desc:
      'Wireless tablet-based spectro for on-press verification. Perfect for label & packaging QC where the operator moves.',
    stats: [
      ['Weight', '0.9 kg'],
      ['Battery', '8h autonomy'],
      ['Modes', 'M0 · M1 · M2'],
    ],
  },
];

const APPLICATIONS = [
  {
    name: 'Wide-format & signage',
    desc: 'Solvent, latex, UV and aqueous. Reference charts on rolls up to 1,650 mm wide.',
    color: '#0F7B3F',
  },
  {
    name: 'Textile & décor',
    desc: 'Direct measurement on fabric, non-woven, wallpaper and floor coverings — no substrate hack needed.',
    color: '#F59E0B',
  },
  {
    name: 'Packaging & labels',
    desc: 'Spot color libraries, brand-owner tolerances, G7 and ISO 12647-6 verification.',
    color: '#0A2A63',
  },
  {
    name: 'Ceramic & décor tile',
    desc: 'The only certified spectro for gloss & matte tile with our polarization option.',
    color: '#FF4E50',
  },
];

const BarbieriPage = () => {
  return (
    <main
      className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden"
      data-testid="barbieri-page"
    >
      <ColorAmbient variant="cool" intensity="medium" />
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      {/* Hero */}
      <section className="container-pn relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0A2A63]/10 text-[#0A2A63] mb-6 shadow-sm"
              data-testid="barbieri-eyebrow"
            >
              <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
              <span className="text-xs font-semibold tracking-widest uppercase">
                Brand Partner · Since 1982 · Italy
              </span>
            </div>
            <img
              src="/logos/barbieri.png"
              alt="Barbieri"
              className="h-16 mb-6 object-contain object-left"
            />
            <h1
              className="text-5xl md:text-7xl font-black text-[#050F2E] tracking-tight leading-[0.95]"
              data-testid="barbieri-headline"
            >
              True color on{' '}
              <span className="pn-gradient-text">every material</span> you print.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Barbieri Electronic builds the reference-grade spectrophotometers used by the world&apos;s
              most demanding print, textile, label and packaging factories. Made in South Tyrol, Italy —
              engineered to give you the same reading, every time, on any substrate.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact">
                <Button
                  data-testid="barbieri-cta-demo"
                  className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition"
                >
                  Request a demo <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/shop">
                <Button
                  data-testid="barbieri-cta-shop"
                  variant="outline"
                  className="rounded-full h-12 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition bg-white/70 backdrop-blur"
                >
                  See instruments
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 max-w-lg gap-4">
              <div>
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">40+</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Years of R&amp;D</p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">M0-M3</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">ISO 13655 modes</p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">ΔE&lt;0.4</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Inter-instrument</p>
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative h-[520px]">
            <div className="absolute inset-0 rounded-[28px] overflow-hidden shadow-2xl bg-gradient-to-br from-[#050F2E] via-[#0A2A63] to-[#0F7B3F]">
              <img
                src="/barbieri/frame_045.webp"
                alt="Barbieri Spectro LFP qb"
                className="absolute inset-0 w-full h-full object-contain p-10"
              />
              <div className="absolute inset-0 grain-overlay opacity-30" />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#050F2E]/95 via-[#050F2E]/40 to-transparent" />

              <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Made in</p>
                <p className="text-lg font-black text-[#0A2A63]">Italy 🇮🇹</p>
              </div>
              <div className="absolute top-5 right-5 bg-[#0F7B3F] text-white rounded-2xl px-4 py-3 shadow-xl">
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Flagship</p>
                <p className="text-sm font-black">Spectro LFP qb</p>
              </div>
              <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-2">
                {['M0', 'M1', 'M2', 'M3', 'OBA-comp', 'Textile', 'Ceramic'].map((c) => (
                  <span
                    key={c}
                    className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-white/95 text-[#0A2A63]"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 h-24 w-24 rounded-full border-2 border-dashed border-[#FF4E50] animate-spin-slow opacity-70" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="container-pn mt-28 relative" data-testid="barbieri-highlights">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">
            Why Barbieri
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight">
            One instrument. Every application.
          </h2>
          <p className="mt-4 text-slate-600">
            Barbieri&apos;s patented motorized measurement head is the reason color scientists,
            brand owners and G7 experts pick these instruments over anything else on the market.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HIGHLIGHTS.map(({ title, desc, Icon }, idx) => (
            <div
              key={title}
              data-testid={`barbieri-highlight-${idx}`}
              className="group bg-white rounded-2xl p-7 border border-slate-100 hover:border-[#0F7B3F] hover:shadow-2xl btn-transition"
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="h-11 w-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(15,123,63,0.14)' }}
                >
                  <Icon className="h-5 w-5" style={{ color: ACCENT }} />
                </span>
                <span className="text-xs font-mono text-slate-300">0{idx + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-[#050F2E]">{title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Device lineup */}
      <section className="container-pn mt-28 relative" data-testid="barbieri-lineup">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">The lineup</p>
            <h2 className="text-3xl md:text-4xl font-black text-[#050F2E] tracking-tight">
              Pick the Spectro that matches your press.
            </h2>
            <p className="mt-4 text-slate-600">
              Each instrument shares the same optical engine — so your color library moves from
              one device to the next without a single re-profile.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1 mt-6 text-sm font-bold text-[#0F7B3F]"
            >
              Not sure which one? Talk to us <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-8 grid md:grid-cols-3 gap-4">
            {DEVICES.map((d) => (
              <div
                key={d.id}
                data-testid={`barbieri-device-${d.id}`}
                className="bg-white rounded-2xl border-2 border-slate-100 p-6 hover:border-[#0F7B3F] hover:shadow-xl btn-transition flex flex-col"
              >
                <span
                  className="self-start text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full text-white mb-4"
                  style={{ backgroundColor: ACCENT }}
                >
                  {d.tag}
                </span>
                <h3 className="text-lg font-black text-[#050F2E]">{d.name}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{d.desc}</p>
                <dl className="mt-4 space-y-2 border-t border-slate-100 pt-4">
                  {d.stats.map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-3">
                      <dt className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                        {k}
                      </dt>
                      <dd className="text-xs font-bold text-[#050F2E]">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications ribbon */}
      <section className="container-pn mt-24 relative" data-testid="barbieri-applications">
        <div className="grid lg:grid-cols-12 gap-10 items-center bg-[#0A2A63] text-white rounded-3xl p-10 md:p-14 shadow-2xl overflow-hidden relative">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#0F7B3F]/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#FF4E50]/15 blur-3xl" />

          <div className="lg:col-span-5 relative">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">
              Where it&apos;s used
            </p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
              From wide-format to ceramic tile — one measurement standard.
            </h2>
            <p className="mt-4 text-slate-300">
              Barbieri is the OEM color engine behind labels, textile, décor, packaging, industrial
              printing and even automotive interior color programs.
            </p>
          </div>
          <div className="lg:col-span-7 relative grid sm:grid-cols-2 gap-3">
            {APPLICATIONS.map((a) => (
              <div
                key={a.name}
                className="bg-white/5 hover:bg-white/10 transition-colors rounded-2xl p-5 border border-white/10 flex gap-4 items-start"
              >
                <span
                  className="mt-1 h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: a.color }}
                />
                <div>
                  <p className="font-bold">{a.name}</p>
                  <p className="text-sm text-slate-300 mt-1 leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pn mt-20 relative">
        <div className="rounded-3xl bg-white border border-slate-100 p-10 md:p-14 shadow-xl overflow-hidden relative">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#0F7B3F]/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">
                Ready for reference-grade color?
              </p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight text-[#050F2E]">
                Talk to a Barbieri specialist at ProofNation.
              </h2>
              <p className="mt-3 text-slate-600">
                We&apos;re Barbieri&apos;s certified partner in the US. We install, calibrate,
                train and service every instrument we sell.
              </p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <Link to="/contact">
                <Button
                  data-testid="barbieri-cta-contact"
                  className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition"
                >
                  Get in touch <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/nix">
                <Button
                  data-testid="barbieri-cta-nix"
                  variant="outline"
                  className="rounded-full h-12 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition"
                >
                  See Nix Sensor
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BarbieriPage;
